// @ts-strict-ignore
import React, { useContext } from 'react';
import {
  ButtonSize,
  ButtonVariant,
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import type { WorkflowOperationMessage } from 'flow/store/engine/types';
import { supportedUndoTypes } from 'flow/store/engine/update/constants';
import { getActionsForAssistance, workflowOperationMessagesByType } from 'flow/service/assistance';
import type { AssistanceButtonAction } from 'flow/service/assistance';
import { StyledAssistanceActions, StyledToolbarButton } from './styles';
import { useAppDispatch } from '~/store/reduxHooks';
import {
  applyPrevWorkflowDataSnapshot,
  clearActionRulesOperatorAmbiguousMode,
  discardRedoStack,
  dismissWorkflowOperationMessage,
} from 'flow/store/redux/slice';
import { WorkflowOperationType } from 'flow/store/engine/constants';
import { resetRuleBuilderPendingGroupStateThunk } from 'flow/store/redux/thunks/rules';
import { RuleBuilderContext } from '../../context';
import { ContainerType } from '../../../types';
import { StyledToolbarContainer, StyledToolbarMessage } from '../styles';
import { UserContext } from '~/dashboard/context';
import { useWorkflowHistoryContext } from 'flow/editor/components/WorkflowHistory/context';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';

const AssistanceToolbar = ({ assistanceMessageObject }: {
  assistanceMessageObject: WorkflowOperationMessage;
}) => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useContext(UserContext);
  const { undo } = useWorkflowHistoryContext();
  const isWorkflowHistoryEnabled = Boolean(featureFlags?.workflowEditorUndoRedo);

  const { sourceAction, containerType } = useContext(RuleBuilderContext);

  const isPreviewAction = sourceAction.type === 'preview';

  const inIsland = containerType === ContainerType.ISLAND;

  const { id, message } = assistanceMessageObject;

  const messageEligibleForUndo = supportedUndoTypes.has(message);

  const actionableMessageOptions = getActionsForAssistance(message);
  const highPriorityMessage = [
    WorkflowOperationType.AMBIGUOUS_RULE_FOUND,
  ].includes(message);

  const messageDescription = workflowOperationMessagesByType[message];

  const onDismiss = () => {
    dispatch(dismissWorkflowOperationMessage({ id }));
  };

  const onUndo = () => {
    if (isWorkflowHistoryEnabled) {
      undo({ skipUiEffect: true });
      dispatch(discardRedoStack());
      onDismiss();
      return;
    }

    dispatch(applyPrevWorkflowDataSnapshot({
      publicId: sourceAction.publicId,
      data: assistanceMessageObject.data,
    }));
  };

  const onCustomActionClick = (action: AssistanceButtonAction) => {
    if (message === WorkflowOperationType.AMBIGUOUS_RULE_FOUND) {
      dispatch(
        createWorkflowHistoryThunk(
          () => dispatch(clearActionRulesOperatorAmbiguousMode({
            id: sourceAction.publicId,
            newRuleOperatorForCorrection: action.value as 'and' | 'or',
            skipSnapshotAndDependencies: isWorkflowHistoryEnabled,
          })),
          isWorkflowHistoryEnabled,
          {
            operation: Operation.Modify,
            uiEffectContext: createUiEffectContext(
              changeDescription.group,
              { actionId: sourceAction.publicId },
            ),
          },
        ),
      );
    }

    if (message === WorkflowOperationType.CANCEL_PENDING_RULE_GROUP) {
      dispatch(resetRuleBuilderPendingGroupStateThunk(sourceAction.publicId));
    }

    // more to come...
  };

  return (
    <StyledToolbarContainer
      isPreviewAction={isPreviewAction}
      inIsland={inIsland}
      highPriority={highPriorityMessage}
    >
      <StyledToolbarMessage>
        <Icon icon={highPriorityMessage ? IconName.Error : IconName.LightbulbHint} color="var(--cl-gray-1100)" size="small" />
        <Text size={2} weight={500}>{messageDescription}</Text>
      </StyledToolbarMessage>
      <StyledAssistanceActions
        inIsland={inIsland}
      >
        {messageEligibleForUndo && (
          <StyledToolbarButton
            icon={IconName.Undo}
            variant={ButtonVariant.tertiary}
            onClick={onUndo}
            size={ButtonSize.xsmall}
          >
            Undo
          </StyledToolbarButton>
        )}
        {actionableMessageOptions?.length > 0
          ? actionableMessageOptions.map(action => (
            <StyledToolbarButton
              variant={ButtonVariant.tertiary}
              onClick={() => onCustomActionClick(action)}
              key={action.id}
              size={ButtonSize.xsmall}
            >
              {action.name}
            </StyledToolbarButton>
          ),
          )
          : (
            <StyledToolbarButton
              icon={IconName.Xmark}
              variant={ButtonVariant.tertiary}
              onClick={onDismiss}
              size={ButtonSize.xsmall}
            >
              Dismiss message
            </StyledToolbarButton>
          )}
      </StyledAssistanceActions>
    </StyledToolbarContainer>
  );
};

export default AssistanceToolbar;
